import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowSquareOut,
    ChartBar,
    CircleNotch,
    DownloadSimple,
    Envelope,
    LockKey,
    MagnifyingGlass,
    SortAscending,
    SortDescending,
    Warning,
} from "@phosphor-icons/react";
import { adminLogin, fetchAdminAnalytics, fetchAdminContacts, fetchAdminSubmissions } from "../lib/api";

const STORAGE_KEY = "biks_admin_password";

function toCsv(rows, columns) {
    const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map((c) => esc(c.label)).join(",");
    const lines = rows.map((r) => columns.map((c) => esc(c.value(r))).join(","));
    return [header, ...lines].join("\n");
}

function downloadCsv(filename, csv) {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

const CONTACT_COLUMNS = [
    { label: "Created", value: (r) => r.created_at },
    { label: "Name", value: (r) => r.name },
    { label: "Email", value: (r) => r.email },
    { label: "Idea", value: (r) => r.idea_one_sentence },
    { label: "Biggest doubt", value: (r) => r.biggest_doubt },
    { label: "Permission to contact", value: (r) => (r.permission_to_contact ? "yes" : "no") },
    { label: "Scan ref", value: (r) => r.submission_id },
];

const SUBMISSION_COLUMNS = [
    { label: "Created", value: (r) => r.created_at },
    { label: "Score", value: (r) => r.core_score },
    { label: "Verdict", value: (r) => r.verdict },
    { label: "Primary kill risk", value: (r) => r.primary_kill_risk },
    { label: "Secondary kill risk", value: (r) => r.secondary_kill_risk },
    { label: "Business type", value: (r) => r.context?.business_type },
    { label: "Industry", value: (r) => r.context?.industry },
    { label: "Stage", value: (r) => r.context?.current_stage },
    { label: "Submission ID", value: (r) => r.id },
];

export default function Admin() {
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [authed, setAuthed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [contacts, setContacts] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [tab, setTab] = useState("contacts");
    const [sortDesc, setSortDesc] = useState(true);
    const [lookupId, setLookupId] = useState("");

    useEffect(() => {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
            void tryLogin(stored, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function tryLogin(pwd, silent = false) {
        setError("");
        if (!silent) setLoading(true);
        try {
            await adminLogin(pwd);
            sessionStorage.setItem(STORAGE_KEY, pwd);
            setPassword(pwd);
            setAuthed(true);
            await loadData(pwd);
        } catch (err) {
            if (!silent) {
                const detail = err?.response?.data?.detail;
                setError(detail || "Invalid password.");
            } else {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }

    async function loadData(pwd) {
        setRefreshing(true);
        setError("");
        try {
            const [c, s, a] = await Promise.all([
                fetchAdminContacts(pwd),
                fetchAdminSubmissions(pwd),
                fetchAdminAnalytics(pwd),
            ]);
            setContacts(c.items || []);
            setSubmissions(s.items || []);
            setAnalytics(a || null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(detail || "Could not load admin data.");
        } finally {
            setRefreshing(false);
        }
    }

    const onSubmit = (e) => {
        e.preventDefault();
        if (!password.trim()) return;
        void tryLogin(password.trim());
    };

    const onLogout = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
        setPassword("");
        setContacts([]);
        setSubmissions([]);
        setAnalytics(null);
    };

    const onLookup = (e) => {
        e.preventDefault();
        const id = lookupId.trim();
        if (id) navigate(`/result/${id}`);
    };

    const sortRows = (rows) => {
        const sorted = [...rows].sort((a, b) =>
            (a.created_at || "").localeCompare(b.created_at || ""),
        );
        return sortDesc ? sorted.reverse() : sorted;
    };

    const visibleContacts = sortRows(contacts);
    const visibleSubmissions = sortRows(submissions);

    const onExport = () => {
        if (tab === "contacts") {
            downloadCsv("biks_contact_requests.csv", toCsv(visibleContacts, CONTACT_COLUMNS));
        } else {
            downloadCsv("biks_submissions.csv", toCsv(visibleSubmissions, SUBMISSION_COLUMNS));
        }
    };

    return (
        <main data-testid="admin-page" className="bg-biks-bg">
            <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
                <Link
                    to="/"
                    data-testid="admin-back-link"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-biks-text3 hover:text-white"
                >
                    <ArrowLeft size={12} weight="bold" /> Back to home
                </Link>
                <div className="overline mt-10">Internal</div>
                <h1 className="font-display mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
                    BoM Admin
                </h1>
                <p className="mt-6 max-w-xl text-base text-biks-text2">
                    Private review requests and scan submissions.
                </p>

                {!authed ? (
                    <form
                        data-testid="admin-login-form"
                        onSubmit={onSubmit}
                        noValidate
                        className="mt-12 grid max-w-md gap-6 rounded-xl border border-biks-line/70 bg-biks-surface/40 p-8"
                    >
                        <label htmlFor="admin-password-input" className="overline block">
                            Admin password
                        </label>
                        <div className="flex items-center gap-3 rounded-md border border-biks-line bg-biks-bg px-4">
                            <LockKey size={14} weight="bold" className="text-biks-text3" />
                            <input
                                id="admin-password-input"
                                data-testid="admin-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full bg-transparent py-4 text-sm text-white placeholder:text-biks-text3 focus:outline-none"
                                autoFocus
                            />
                        </div>
                        {error && (
                            <div
                                data-testid="admin-error"
                                className="flex items-start gap-2 rounded-md border border-biks-danger/60 bg-biks-danger/5 p-3 text-sm text-biks-danger"
                            >
                                <Warning size={14} weight="bold" className="mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}
                        <button
                            type="submit"
                            data-testid="admin-login-button"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-biks-blue px-6 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <CircleNotch size={14} weight="bold" className="animate-spin" />
                                    Verifying...
                                </>
                            ) : (
                                "Sign in"
                            )}
                        </button>
                    </form>
                ) : (
                    <div data-testid="admin-dashboard" className="mt-12">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex rounded-md border border-biks-line/70 bg-biks-surface/40 p-1">
                                <button
                                    type="button"
                                    data-testid="admin-tab-contacts"
                                    onClick={() => setTab("contacts")}
                                    className={`rounded px-4 py-2 text-xs uppercase tracking-[0.16em] transition-colors ${
                                        tab === "contacts"
                                            ? "bg-biks-blue text-white"
                                            : "text-biks-text2 hover:text-white"
                                    }`}
                                >
                                    Requests ({contacts.length})
                                </button>
                                <button
                                    type="button"
                                    data-testid="admin-tab-submissions"
                                    onClick={() => setTab("submissions")}
                                    className={`rounded px-4 py-2 text-xs uppercase tracking-[0.16em] transition-colors ${
                                        tab === "submissions"
                                            ? "bg-biks-blue text-white"
                                            : "text-biks-text2 hover:text-white"
                                    }`}
                                >
                                    Scans ({submissions.length})
                                </button>
                                <button
                                    type="button"
                                    data-testid="admin-tab-analytics"
                                    onClick={() => setTab("analytics")}
                                    className={`rounded px-4 py-2 text-xs uppercase tracking-[0.16em] transition-colors ${
                                        tab === "analytics"
                                            ? "bg-biks-blue text-white"
                                            : "text-biks-text2 hover:text-white"
                                    }`}
                                >
                                    Analytics
                                </button>
                            </div>
                            <div className="flex items-center gap-3">
                                {tab !== "analytics" && (
                                  <>
                                <button
                                    type="button"
                                    data-testid="admin-sort-button"
                                    onClick={() => setSortDesc((v) => !v)}
                                    className="inline-flex items-center gap-2 rounded-md border border-biks-line px-4 py-2 text-xs uppercase tracking-[0.18em] text-biks-text2 hover:border-biks-blueSoft hover:text-white"
                                >
                                    {sortDesc ? (
                                        <SortDescending size={12} weight="bold" />
                                    ) : (
                                        <SortAscending size={12} weight="bold" />
                                    )}
                                    {sortDesc ? "Newest first" : "Oldest first"}
                                </button>
                                <button
                                    type="button"
                                    data-testid="admin-export-csv-button"
                                    onClick={onExport}
                                    className="inline-flex items-center gap-2 rounded-md border border-biks-line px-4 py-2 text-xs uppercase tracking-[0.18em] text-biks-text2 hover:border-biks-blueSoft hover:text-white"
                                >
                                    <DownloadSimple size={12} weight="bold" />
                                    Export CSV
                                </button>
                                  </>
                                )}
                                <button
                                    type="button"
                                    data-testid="admin-refresh-button"
                                    onClick={() => loadData(password)}
                                    disabled={refreshing}
                                    className="inline-flex items-center gap-2 rounded-md border border-biks-line px-4 py-2 text-xs uppercase tracking-[0.18em] text-biks-text2 hover:border-biks-blueSoft hover:text-white disabled:opacity-50"
                                >
                                    {refreshing ? (
                                        <CircleNotch size={12} weight="bold" className="animate-spin" />
                                    ) : null}
                                    Refresh
                                </button>
                                <button
                                    type="button"
                                    data-testid="admin-logout-button"
                                    onClick={onLogout}
                                    className="inline-flex items-center gap-2 rounded-md border border-biks-line px-4 py-2 text-xs uppercase tracking-[0.18em] text-biks-text3 hover:border-biks-danger hover:text-biks-danger"
                                >
                                    Sign out
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div
                                data-testid="admin-error"
                                className="mt-6 flex items-start gap-2 rounded-md border border-biks-danger/60 bg-biks-danger/5 p-3 text-sm text-biks-danger"
                            >
                                <Warning size={14} weight="bold" className="mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}

                        <form
                            data-testid="admin-lookup-form"
                            onSubmit={onLookup}
                            className="mt-8 flex flex-col gap-3 rounded-xl border border-biks-line/70 bg-biks-surface/40 p-5 sm:flex-row sm:items-center"
                        >
                            <label htmlFor="admin-lookup-input" className="overline shrink-0">
                                Open result by ID
                            </label>
                            <input
                                id="admin-lookup-input"
                                data-testid="admin-lookup-input"
                                value={lookupId}
                                onChange={(e) => setLookupId(e.target.value)}
                                placeholder="Paste a BoM result ID from a review request"
                                className="w-full rounded-md border border-biks-line bg-biks-bg px-4 py-3 text-sm text-white placeholder:text-biks-text3 focus:border-biks-blue focus:outline-none"
                            />
                            <button
                                type="submit"
                                data-testid="admin-lookup-button"
                                disabled={!lookupId.trim()}
                                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-biks-blue px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-blue-500 disabled:opacity-40"
                            >
                                <MagnifyingGlass size={12} weight="bold" />
                                Open result
                            </button>
                        </form>

                        {tab === "analytics" ? (
                            <AnalyticsPanel analytics={analytics} />
                        ) : tab === "contacts" ? (
                            visibleContacts.length === 0 ? (
                                <div
                                    data-testid="admin-empty"
                                    className="mt-8 rounded-xl border border-dashed border-biks-line/70 bg-biks-surface/30 p-10 text-center text-sm text-biks-text2"
                                >
                                    No private review requests yet.
                                </div>
                            ) : (
                                <ul data-testid="admin-contact-list" className="mt-8 space-y-4">
                                    {visibleContacts.map((c) => (
                                        <ContactItem key={c.id} item={c} />
                                    ))}
                                </ul>
                            )
                        ) : visibleSubmissions.length === 0 ? (
                                <div
                                    data-testid="admin-submissions-empty"
                                    className="mt-8 rounded-xl border border-dashed border-biks-line/70 bg-biks-surface/30 p-10 text-center text-sm text-biks-text2"
                                >
                                    No scan submissions yet.
                                </div>
                            ) : (
                                <ul data-testid="admin-submission-list" className="mt-8 space-y-4">
                                    {visibleSubmissions.map((s) => (
                                        <SubmissionItem key={s.id} item={s} />
                                    ))}
                                </ul>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
}

function AnalyticsPanel({ analytics }) {
    const funnel = analytics?.funnel || [];
    if (funnel.length === 0) {
        return (
            <div
                data-testid="admin-analytics-empty"
                className="mt-8 rounded-xl border border-dashed border-biks-line/70 bg-biks-surface/30 p-10 text-center text-sm text-biks-text2"
            >
                No analytics yet.
            </div>
        );
    }
    const max = Math.max(...funnel.map((f) => f.value || 0), 1);
    return (
        <div data-testid="admin-analytics-panel" className="mt-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {funnel.map((f) => (
                    <div
                        key={f.key}
                        data-testid={`admin-analytics-${f.key}`}
                        className="rounded-xl border border-biks-line/70 bg-biks-surface/40 p-5"
                    >
                        <div className="overline">{f.label}</div>
                        <div
                            data-testid={`admin-analytics-${f.key}-value`}
                            className="font-mono-data mt-3 text-3xl font-semibold text-white"
                        >
                            {f.value}
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-8 rounded-xl border border-biks-line/70 bg-biks-surface/40 p-6">
                <div className="overline mb-5 flex items-center gap-2">
                    <ChartBar size={14} weight="bold" />
                    Conversion funnel
                </div>
                <div className="space-y-4">
                    {funnel.map((f) => (
                        <div key={f.key} className="flex items-center gap-4">
                            <div className="w-32 shrink-0 text-sm text-biks-text2">{f.label}</div>
                            <div className="h-3 flex-1 rounded-full bg-biks-bg">
                                <div
                                    className="h-3 rounded-full bg-gradient-to-r from-biks-blue to-biks-blueSoft"
                                    style={{ width: `${Math.round(((f.value || 0) / max) * 100)}%` }}
                                />
                            </div>
                            <div className="w-10 shrink-0 text-right font-mono-data text-sm text-white">
                                {f.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ContactItem({ item }) {
    const created = item.created_at
        ? new Date(item.created_at).toLocaleString()
        : "—";
    return (
        <li
            data-testid="admin-contact-item"
            className="rounded-xl border border-biks-line/70 bg-biks-surface/40 p-6"
        >
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="overline">{created}</div>
                    <h3
                        data-testid="admin-contact-name"
                        className="mt-2 text-lg font-semibold text-white"
                    >
                        {item.name}
                    </h3>
                    <a
                        data-testid="admin-contact-email"
                        href={`mailto:${item.email}`}
                        className="mt-1 inline-flex items-center gap-2 text-sm text-biks-blueSoft hover:text-biks-blue"
                    >
                        <Envelope size={12} weight="bold" /> {item.email}
                    </a>
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-biks-text3">
                    {item.permission_to_contact ? (
                        <span className="text-biks-ok">Contact OK</span>
                    ) : (
                        <span className="text-biks-warn">No contact permission</span>
                    )}
                </div>
            </div>
            {item.idea_one_sentence ? (
                <div className="mt-5">
                    <div className="overline mb-2">Idea</div>
                    <p className="text-sm text-biks-text2">{item.idea_one_sentence}</p>
                </div>
            ) : null}
            <div className="mt-5">
                <div className="overline mb-2">Biggest doubt</div>
                <p className="whitespace-pre-wrap text-sm text-white">
                    {item.biggest_doubt}
                </p>
            </div>
            {item.submission_id ? (
                <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-biks-text3">
                    <span className="overline">Scan ref</span>
                    <code className="rounded bg-biks-bg px-2 py-1 text-biks-text2">
                        {item.submission_id}
                    </code>
                    <Link
                        to={`/result/${item.submission_id}`}
                        data-testid="admin-contact-open-diagnostic"
                        className="inline-flex items-center gap-1.5 text-biks-blueSoft hover:text-biks-blue"
                    >
                        <ArrowSquareOut size={12} weight="bold" />
                        Open diagnostic
                    </Link>
                </div>
            ) : null}
        </li>
    );
}

function SubmissionItem({ item }) {
    const created = item.created_at
        ? new Date(item.created_at).toLocaleString()
        : "—";
    const ctx = item.context || {};
    return (
        <li
            data-testid="admin-submission-item"
            className="rounded-xl border border-biks-line/70 bg-biks-surface/40 p-6"
        >
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="overline">{created}</div>
                    <div className="mt-2 flex items-baseline gap-3">
                        <span
                            data-testid="admin-submission-score"
                            className="font-mono-data text-3xl font-semibold text-white"
                        >
                            {item.core_score == null ? "—" : item.core_score}
                        </span>
                        <span className="text-xs uppercase tracking-[0.16em] text-biks-text3">
                            /100
                        </span>
                    </div>
                    {item.verdict && (
                        <div
                            data-testid="admin-submission-verdict"
                            className="mt-2 text-sm font-semibold text-biks-blueSoft"
                        >
                            {item.verdict}
                        </div>
                    )}
                </div>
                <div className="text-right text-xs text-biks-text3">
                    <div>{ctx.business_type} · {ctx.industry}</div>
                    <div className="mt-1">{ctx.current_stage}</div>
                </div>
            </div>
            <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                <div>
                    <span className="overline mr-2">Primary risk</span>
                    <span className="text-biks-text2">
                        {item.primary_kill_risk || "None"}
                    </span>
                </div>
                <div>
                    <span className="overline mr-2">Secondary risk</span>
                    <span className="text-biks-text2">
                        {item.secondary_kill_risk || "None"}
                    </span>
                </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-biks-text3">
                <code className="rounded bg-biks-bg px-2 py-1 text-biks-text2">
                    {item.id}
                </code>
                <Link
                    to={`/result/${item.id}`}
                    data-testid="admin-submission-open-result"
                    className="inline-flex items-center gap-1.5 text-biks-blueSoft hover:text-biks-blue"
                >
                    <ArrowSquareOut size={12} weight="bold" />
                    Open result
                </Link>
            </div>
        </li>
    );
}
